import { Container, Graphics, Text } from 'pixi.js';
import type { LineWin, MathConfig, ResolvedTheme } from '../types';
import { toPixiColor } from '../util/color';
import type { Timeline } from './tween';

type Cell = (reel: number, row: number) => { x: number; y: number };
type Pt = { x: number; y: number };

/** Deterministic pseudo-random for stable jagged bolts (re-drawn on resize without jumping). */
function prng(seed: number): () => number {
  let s = (seed * 9301 + 49297) % 233280;
  return () => (s = (s * 9301 + 49297) % 233280) / 233280;
}

/** Draws winning paylines: 'solid' coloured polylines, or Lucky Fruit 'lightning' bolts with sparks. */
export class LineLayer extends Container {
  private theme: ResolvedTheme;
  private readonly math: MathConfig;
  private readonly timeline: Timeline | null;
  private readonly gfx = new Graphics();
  private readonly fx = new Graphics();
  private readonly badges = new Container();
  private stopAnim: (() => void) | null = null;
  private bolts: Array<{ pts: Pt[]; sparks: Pt[]; color: number; delay: number }> = [];
  private cellSize = { w: 72, h: 72 };

  constructor(o: { theme: ResolvedTheme; math: MathConfig; timeline?: Timeline }) {
    super();
    this.theme = o.theme;
    this.math = o.math;
    this.timeline = o.timeline ?? null;
    this.addChild(this.gfx, this.fx, this.badges);
    this.eventMode = 'none';
  }

  private colorOf(lineIndex: number): number {
    const list = this.theme.palette.paylines;
    return toPixiColor(list[lineIndex % list.length]);
  }

  showLines(wins: LineWin[], cell: Cell, size: { w: number; h: number }): void {
    this.clear();
    this.cellSize = size;
    this.alpha = 1;
    if (this.theme.animations.effects.line.style === 'lightning') {
      wins.forEach((win, i) => this.addBolt(win, cell, i));
      this.drawBolts(1, 1);
      this.startBoltAnimation();
    } else for (const win of wins) this.drawWin(win, cell, size);
  }

  showLine(win: LineWin, cell: Cell, size: { w: number; h: number }): void {
    this.clear();
    this.cellSize = size;
    this.alpha = 1;
    if (this.theme.animations.effects.line.style === 'lightning') {
      this.addBolt(win, cell, 0);
      this.drawBolts(1, 1);
      this.startBoltAnimation();
    } else this.drawWin(win, cell, size);
  }

  // ------------------------------------------------------------------ solid

  private drawWin(win: LineWin, cell: Cell, size: { w: number; h: number }): void {
    const rows = this.math.paylines[win.lineIndex];
    if (!rows) return;
    const color = this.colorOf(win.lineIndex);
    const { width, alpha, glow } = this.theme.animations.effects.line;
    const pts = rows.map((row, reel) => cell(reel, row));
    const first = { x: pts[0].x - size.w / 2 - 4, y: pts[0].y };
    const last = { x: pts[pts.length - 1].x + size.w / 2 + 4, y: pts[pts.length - 1].y };
    const path = [first, ...pts, last];
    if (glow) {
      this.gfx.moveTo(path[0].x, path[0].y);
      for (const p of path.slice(1)) this.gfx.lineTo(p.x, p.y);
      this.gfx.stroke({ color, alpha: 0.35, width: width * 2.4, cap: 'round', join: 'round' });
    }
    this.gfx.moveTo(path[0].x, path[0].y);
    for (const p of path.slice(1)) this.gfx.lineTo(p.x, p.y);
    this.gfx.stroke({ color, alpha, width, cap: 'round', join: 'round' });
    for (const [reel, row] of win.positions) {
      const c = cell(reel, row);
      this.gfx.roundRect(c.x - size.w / 2 + 2, c.y - size.h / 2 + 2, size.w - 4, size.h - 4, 10).stroke({ color, alpha: 1, width: Math.max(3, width * 0.7) });
    }
    const r = Math.max(10, size.w * 0.16);
    const badge = new Graphics().circle(0, 0, r).fill({ color }).circle(0, 0, r).stroke({ color: 0x000000, alpha: 0.35, width: 2 });
    const label = new Text({
      text: String(win.lineIndex + 1),
      style: { fontFamily: this.theme.fonts.ui.family, fontSize: r * 1.1, fontWeight: '700', fill: 0x000000 },
    });
    label.anchor.set(0.5);
    const holder = new Container();
    holder.addChild(badge, label);
    holder.position.set(first.x - r * 0.4, first.y);
    this.badges.addChild(holder);
  }

  // ------------------------------------------------------------------ lightning

  /** Jagged path through the winning cells (only the paying part of the line), plus spark points. */
  private addBolt(win: LineWin, cell: Cell, order: number): void {
    const rows = this.math.paylines[win.lineIndex];
    if (!rows) return;
    const count = Math.max(2, win.positions.length);
    const centers = rows.slice(0, count).map((row, reel) => cell(reel, row));
    const rnd = prng(win.lineIndex * 131 + count * 17);
    const jitter = this.cellSize.h * 0.16;
    const pts: Pt[] = [centers[0]];
    for (let i = 1; i < centers.length; i++) {
      const a = centers[i - 1];
      const b = centers[i];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      const segs = 3;
      for (let s = 1; s < segs; s++) {
        const u = s / segs;
        const off = (rnd() - 0.5) * 2 * jitter;
        pts.push({ x: a.x + dx * u + nx * off, y: a.y + dy * u + ny * off });
      }
      pts.push(b);
    }
    this.bolts.push({ pts, sparks: centers, color: this.colorOf(win.lineIndex), delay: order * 100 });
  }

  /** Draw all bolts; `flicker` (0..1) modulates the bolt layer, `spark` (0..1) the spark scale phase. */
  private drawBolts(flicker: number, sparkPhase: number): void {
    const g = this.fx;
    g.clear();
    const p = this.theme.palette;
    const primary = toPixiColor(p.winNormal);
    const k = Math.max(0.6, this.cellSize.w / 110);
    for (const b of this.bolts) {
      const accent = b.color;
      const path = (width: number, color: number, alpha: number) => {
        g.moveTo(b.pts[0].x, b.pts[0].y);
        for (const q of b.pts.slice(1)) g.lineTo(q.x, q.y);
        g.stroke({ width: width * k, color, alpha, cap: 'round', join: 'round' });
      };
      // base: whole line through the cells (soft), halo, bolt, core
      path(26, primary, 0.14);
      path(19, primary, 0.34);
      path(11, accent, 0.5 * flicker + 0.2);
      path(7, accent, 0.85 * flicker + 0.15);
      path(2.2, 0xf4ffff, 0.95);
      // sparks at the winning cell centres (scale pulse 0.2 -> 1.7 -> 0.75 -> 2.5 fade)
      b.sparks.forEach((c, i) => {
        const ph = (sparkPhase + i * 0.08) % 1;
        let scale: number;
        let alpha: number;
        if (ph < 0.12) {
          scale = 0.2 + (ph / 0.12) * 0.4;
          alpha = ph / 0.12;
        } else if (ph < 0.23) {
          scale = 0.6 + ((ph - 0.12) / 0.11) * 1.1;
          alpha = 1;
        } else if (ph < 0.38) {
          scale = 1.7 - ((ph - 0.23) / 0.15) * 0.95;
          alpha = 0.7;
        } else if (ph < 0.55) {
          scale = 0.75 + ((ph - 0.38) / 0.17) * 1.75;
          alpha = 0.7 * (1 - (ph - 0.38) / 0.17);
        } else {
          scale = 0;
          alpha = 0;
        }
        if (alpha > 0) {
          const r = 4 * k * scale;
          g.circle(c.x, c.y, r * 2.2).fill({ color: primary, alpha: alpha * 0.35 });
          g.circle(c.x, c.y, r).fill({ color: 0xffffff, alpha });
        }
      });
    }
  }

  private startBoltAnimation(): void {
    this.stopAnim?.();
    this.stopAnim = null;
    if (!this.timeline) return;
    let elapsed = 0;
    this.stopAnim = this.timeline.loop({
      period: 1050,
      update: (ph, dt) => {
        elapsed += dt;
        // flicker: 740ms cycle in 3 steps (1, .46, .88) after the draw-in
        const f = (elapsed % 740) / 740;
        const flicker = f < 0.2 ? 1 : f < 0.22 ? 0.46 : f < 0.24 ? 0.88 : f < 0.62 ? 1 : f < 0.66 ? 0.46 : f < 0.69 ? 0.88 : 1;
        this.drawBolts(flicker, ph);
      },
    });
  }

  clear(): void {
    this.stopAnim?.();
    this.stopAnim = null;
    this.bolts = [];
    this.gfx.clear();
    this.fx.clear();
    this.badges.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.alpha = 1;
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
  }
}
